/**
 * Fetch layer for the local API and third-party geocoders.
 * - identical concurrent GETs share one network request (reference-counted:
 *   the underlying request is aborted only when every caller has aborted);
 * - per-call AbortSignal + timeout;
 * - a global "busy" signal drives the top progress bar (with a short delay
 *   so fast responses do not flash it).
 */

import { noteUpstream } from './status.js';
import { track } from './telemetry.js';
import { compute, warmCompute } from './compute.js';

const inflight = new Map();
let busyCount = 0;
const busyListeners = new Set();
export const onBusy = (fn) => busyListeners.add(fn);
function busy(d) {
  const was = busyCount > 0;
  busyCount = Math.max(0, busyCount + d);
  const is = busyCount > 0;
  if (was !== is) for (const fn of busyListeners) fn(is);
}

export class ApiError extends Error {
  constructor(message, status = 0, body = null) { super(message); this.status = status; this.body = body; }
}
export const isAbort = (e) => !!e && e.name === 'AbortError';

function friendly(status, body) {
  if (body && body.error) return body.error;
  if (status === 429) return 'Bạn thao tác quá nhanh, vui lòng thử lại sau giây lát.';
  if (status >= 500) return 'Máy chủ dữ liệu quy hoạch đang bận, vui lòng thử lại.';
  if (status === 404) return 'Không tìm thấy dữ liệu.';
  return `Lỗi ${status}`;
}

function sharedFetch(url, timeout, init) {
  let e = inflight.get(url);
  if (e) return e;
  const ctl = new AbortController();
  e = { ctl, refs: 0 };
  const timer = setTimeout(() => ctl.abort(new DOMException('Hết thời gian chờ phản hồi', 'TimeoutError')), timeout);
  e.promise = fetch(url, { ...init, signal: ctl.signal })
    .then(async (r) => {
      const ct = r.headers.get('content-type') || '';
      const body = ct.includes('json') ? await r.json().catch(() => null) : await r.text().catch(() => null);
      if (!r.ok) throw new ApiError(friendly(r.status, body), r.status, body);
      return body;
    }, (err) => {
      if (err && err.name === 'TimeoutError') throw new ApiError('Hết thời gian chờ phản hồi từ máy chủ.', 0);
      if (err && err.name === 'AbortError') throw err;
      throw new ApiError('Không kết nối được máy chủ. Kiểm tra kết nối mạng.', 0);
    })
    .finally(() => { clearTimeout(timer); if (inflight.get(url) === e) inflight.delete(url); });
  e.promise.catch(() => {});
  inflight.set(url, e);
  return e;
}

/** GET JSON with sharing, abort and timeout. */
export function getJSON(url, { signal, timeout = 30000, track = true, headers } = {}) {
  if (signal?.aborted) return Promise.reject(signal.reason || new DOMException('Aborted', 'AbortError'));
  const e = sharedFetch(url, timeout, { headers: { accept: 'application/json', ...(headers || {}) } });
  e.refs++;
  if (track) busy(+1);
  return new Promise((resolve, reject) => {
    let settled = false;
    const settle = () => {
      if (settled) return false;
      settled = true;
      if (track) busy(-1);
      signal?.removeEventListener('abort', onAbort);
      return true;
    };
    const onAbort = () => {
      if (!settle()) return;
      if (--e.refs <= 0) e.ctl.abort();
      reject(signal.reason || new DOMException('Aborted', 'AbortError'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
    e.promise.then((v) => { if (settle()) { e.refs--; resolve(v); } }, (err) => { if (settle()) { e.refs--; reject(err); } });
  });
}

export async function postJSON(url, data, { signal, timeout = 60000 } = {}) {
  busy(+1);
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(new DOMException('timeout', 'TimeoutError')), timeout);
  const onAbort = () => ctl.abort(signal.reason);
  signal?.addEventListener('abort', onAbort, { once: true });
  try {
    const r = await fetch(url, { method: 'POST', headers: { 'content-type': 'application/json', accept: 'application/json' }, body: JSON.stringify(data), signal: ctl.signal });
    const body = await r.json().catch(() => null);
    if (!r.ok) throw new ApiError(friendly(r.status, body), r.status, body);
    return body;
  } catch (err) {
    if (err instanceof ApiError || err.name === 'AbortError' && signal?.aborted) throw err;
    if (err.name === 'TimeoutError' || err.name === 'AbortError') throw new ApiError('Hết thời gian chờ phân tích. Thử lại với khu đất nhỏ hơn.', 0);
    throw new ApiError('Không kết nối được máy chủ.', 0);
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', onAbort);
    busy(-1);
  }
}

/* ---------------- backends ---------------- */

/**
 * Two interchangeable backends with identical result shapes:
 *  - "server" (default): the Node server computes everything at /api/*;
 *  - "edge" (static hosting, e.g. Cloudflare Pages; enabled by <meta name="qh-backend" content="edge">):
 *    the planning engine runs in this page and only raw official data passes through /api/up/*.
 */
export const EDGE = typeof document !== 'undefined' && document.querySelector('meta[name="qh-backend"]')?.content === 'edge';
/** The engine (engine/browser.js) lives in the compute worker (compute.js): same calls, plain data. */
const EDGE_CALLS = ['meta', 'projects', 'point', 'qh100', 'nearest', 'nearby', 'section', 'transportMasks', 'cells', 'labels', 'feature', 'analyze'];
const engineProxy = Object.fromEntries(EDGE_CALLS.map((name) => [name, (...args) => compute('edge', [name, args])]));
const engine = () => Promise.resolve(engineProxy);
if (EDGE) setTimeout(warmCompute, 0);

/**
 * Run an engine call with the same busy / abort / error semantics as getJSON.
 * `up`: name of a call that needs the official API (feeds the outage banner and error statistics).
 */
function viaEngine(fn, { signal, track: busyTrack = true, timeout = 60000, up = null } = {}) {
  if (signal?.aborted) return Promise.reject(signal.reason || new DOMException('Aborted', 'AbortError'));
  if (busyTrack) busy(+1);
  return new Promise((resolve, reject) => {
    let settled = false;
    const done = () => {
      if (settled) return false;
      settled = true;
      clearTimeout(timer);
      if (busyTrack) busy(-1);
      signal?.removeEventListener('abort', onAbort);
      return true;
    };
    const failed = (err) => {
      if (up) { noteUpstream(false, err); track('api', up, err.status ? String(err.status) : 'net'); }
      reject(err);
    };
    const onAbort = () => { if (done()) reject(signal.reason || new DOMException('Aborted', 'AbortError')); };
    const timer = setTimeout(() => { if (done()) failed(new ApiError('Hết thời gian chờ phản hồi từ máy chủ.', 0)); }, timeout);
    signal?.addEventListener('abort', onAbort, { once: true });
    engine().then(fn).then(
      (v) => { if (done()) { if (up) noteUpstream(true); resolve(v); } },
      (e) => { if (done()) failed(e instanceof ApiError ? e : new ApiError(e?.status ? e.message : 'Không kết nối được máy chủ. Kiểm tra kết nối mạng.', e?.status || 0, { error: e?.message, issues: e?.issues })); },
    );
  });
}

/* ---------------- local API ---------------- */

const r6 = (v) => (+v).toFixed(6);
const memo = new Map();
const once = (key, fn) => { if (!memo.has(key)) memo.set(key, fn().catch((e) => { memo.delete(key); throw e; })); return memo.get(key); };

const serverApi = {
  meta: () => once('meta', () => getJSON('/api/meta', { track: false })),
  projects: () => once('projects', () => getJSON('/api/projects', { track: false })).then((j) => j.items || []),
  point: (lat, lng, opts) => getJSON(`/api/point?lat=${r6(lat)}&lng=${r6(lng)}`, opts),
  qh100: (lat, lng, opts) => getJSON(`/api/qh100?lat=${r6(lat)}&lng=${r6(lng)}`, { timeout: 25000, ...opts }),
  nearest: (lat, lng, opts) => getJSON(`/api/nearest?lat=${r6(lat)}&lng=${r6(lng)}`, { track: false, timeout: 20000, ...opts }),
  nearby: (lat, lng, radius = 500, opts, set = 'facility') => getJSON(`/api/nearby?lat=${r6(lat)}&lng=${r6(lng)}&radius=${radius}${set === 'transport' ? '&set=transport' : ''}`, { track: false, ...opts }),
  section: (lat, lng, layerNo = 654, opts) => getJSON(`/api/section?lat=${r6(lat)}&lng=${r6(lng)}&layer=${layerNo}`, { track: false, timeout: 20000, ...opts }),
  transportMasks: () => once('tmasks', () => getJSON('/api/transport-masks', { track: false, timeout: 90000 })),
  cells: (bbox, opts) => getJSON(`/api/cells?bbox=${bbox.map((v) => (+v).toFixed(6)).join(',')}`, { track: false, timeout: 30000, ...opts }),
  labels: (x, y, z = 14, opts) => getJSON(`/api/labels/${z}/${x}/${y}`, { track: false, timeout: z === 12 ? 70000 : 40000, ...opts }),
  feature: (id, opts) => getJSON(`/api/feature/${encodeURIComponent(id)}`, opts),
  analyze: (ring, zone, opts) => postJSON('/api/analyze', { ring, zone }, opts),
  resolve: (url, opts) => getJSON(`/api/resolve?url=${encodeURIComponent(url)}`, opts),
};

const edgeApi = {
  meta: () => once('meta', () => viaEngine((e) => e.meta(), { track: false })),
  projects: () => once('projects', () => viaEngine((e) => e.projects(), { track: false, timeout: 90000 })).then((j) => j.items || []),
  point: (lat, lng, opts) => viaEngine((e) => e.point(+r6(lat), +r6(lng)), { up: 'point', ...opts }),
  qh100: (lat, lng, opts) => viaEngine((e) => e.qh100(+r6(lat), +r6(lng)), { timeout: 25000, up: 'qh100', ...opts }),
  nearest: (lat, lng, opts) => viaEngine((e) => e.nearest(+r6(lat), +r6(lng)), { track: false, timeout: 20000, up: 'nearest', ...opts }),
  nearby: (lat, lng, radius = 500, opts, set = 'facility') => viaEngine((e) => e.nearby(+r6(lat), +r6(lng), radius, set), { track: false, ...opts }),
  section: (lat, lng, layerNo = 654, opts) => viaEngine((e) => e.section(+r6(lat), +r6(lng), layerNo), { track: false, timeout: 20000, up: 'section', ...opts }),
  transportMasks: () => once('tmasks', () => viaEngine((e) => e.transportMasks(), { track: false, timeout: 120000 })),
  cells: (bbox, opts) => viaEngine((e) => e.cells(bbox.map(Number)), { track: false, timeout: 30000, up: 'cells', ...opts }),
  labels: (x, y, z = 14, opts) => viaEngine((e) => e.labels(x, y, z), { track: false, timeout: z === 12 ? 70000 : 40000, ...opts }),
  feature: (id, opts) => viaEngine((e) => e.feature(id), { up: 'feature', ...opts }),
  analyze: (ring, zone, opts) => viaEngine((e) => e.analyze(ring, zone), { timeout: 90000, up: 'analyze', ...opts }),
  resolve: serverApi.resolve,
};

export const api = EDGE ? edgeApi : serverApi;

/** Runtime client settings (e.g. { esriKey }) – same endpoint on the Node server and the edge worker. */
export const clientConfig = () => once('config', () => getJSON('/api/config', { track: false, timeout: 4000 })).catch(() => ({}));
